import { Component } from 'react';
import dynamic from 'next/dynamic';
import { getProPricingMenus } from 'modules/users/service';

import ProBreadcrumbs from '../ProBreadcrumbs';
import ProProfile from '../ProProfile';
import { ProProfilePro, PricingMenu } from '../../types';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';

import styles from './ProProfileContent.module.scss';

const JobTypeSelectModal = dynamic(
  () => import(/* webpackChunkName: "job-type-select-modal" */ '../JobTypeSelectModal')
);

interface Props {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  jobTypeId?: string;
  loadRelated: ({
    jobTypeId,
    requestableOnly,
  }: {
    jobTypeId: string;
    requestableOnly: boolean;
  }) => void;
  openRequestModal: (jobTypeId: string) => void;
  pro: ProProfilePro;
  requestableJobTypes: Array<{
    id: string;
    title: string;
  }>;
  setJobTypeTitle: (jobTypeId: string) => void;
  slugFromQueryParam: string;
}

interface State {
  isOpen: boolean;
  pricingMenus: Array<PricingMenu>;
}

class ProProfileContent extends Component<Props, State> {
  static defaultProps = {
    isAuthenticated: false,
    pro: undefined,
    requestableJobTypes: [],
    validItp: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
      pricingMenus: [],
    };
  }

  componentDidMount() {
    const { loadRelated, pro } = this.props;
    if (pro.enabledJobTypes && pro.enabledJobTypes.length === 1) {
      loadRelated({
        jobTypeId: pro.enabledJobTypes[0].id,
        requestableOnly: true,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getProPricingMenus({ userId: pro.id }).then(
      (
        pricingMenusByJobTypes: Array<{
          items: Array<{
            description: string;
            key: string;
            price: number;
          }>;
          jobTypeId: string;
          title: string;
        }>
      ) => {
        const pricingMenus = pricingMenusByJobTypes.flatMap((pricingMenusByJobType) => ({
          jobTypeId: pricingMenusByJobType.jobTypeId,
          pricingMenus: pricingMenusByJobType.items.map((item) => ({
            ...item,
            title: pricingMenusByJobType.title,
          })),
        }));

        this.setState({ pricingMenus });
      }
    );
  }

  onJobTypeSelected(jobTypeId: string) {
    const { openRequestModal } = this.props;
    this.setState({
      isOpen: false,
    });
    openRequestModal(jobTypeId);
  }

  onRequestClicked() {
    this.setState({
      isOpen: true,
    });
  }

  render() {
    const {
      pro,
      featureFlags,
      internalBlogPosts,
      jobTypeId,
      requestableJobTypes,
      slugFromQueryParam,
      setJobTypeTitle,
    } = this.props;
    const { isOpen, pricingMenus } = this.state;
    const defaultJobTypes = requestableJobTypes.filter((jobType) => jobType.id === jobTypeId);
    const defaultJobTypeId = defaultJobTypes.length ? defaultJobTypes[0].id : undefined;
    const preferredJobTypeWithUserSelection = [
      ...defaultJobTypes,
      ...requestableJobTypes.filter((jobType) => jobType.id !== jobTypeId),
    ];

    const jobTypes = pro.jobProfiles.map((jobProfile) => ({
      categories: jobProfile.categories,
      categoryTranslation: jobProfile.categoryTranslation,
      id: jobProfile.jobTypeId,
      title: jobProfile.label,
    }));

    const mainJobType =
      jobTypes.find((jobType) => jobType.id === (jobTypeId ?? pro.proProfile.mainJobType)) ??
      jobTypes[0];

    return (
      <>
        <div className={styles.wrapper}>
          <ProBreadcrumbs jobType={mainJobType} pro={pro} />
          <ProProfile
            internalBlogPosts={internalBlogPosts}
            featureFlags={featureFlags}
            jobTypeId={jobTypeId}
            // eslint-disable-next-line react/jsx-no-bind
            onRequestClicked={() => this.onRequestClicked()}
            pro={{ ...pro, pricingMenus, slug: slugFromQueryParam }}
            setJobTypeTitle={setJobTypeTitle}
          />
        </div>

        {isOpen && (
          <JobTypeSelectModal
            // eslint-disable-next-line react/jsx-no-bind
            dismiss={() => this.setState({ isOpen: false })}
            initialValues={{ jobTypeId: defaultJobTypeId }}
            jobTypes={preferredJobTypeWithUserSelection}
            // eslint-disable-next-line react/jsx-no-bind
            onSubmit={({ jobTypeId: selectedJobTypeId }: { jobTypeId: string }) =>
              this.onJobTypeSelected(selectedJobTypeId)
            }
            proName={pro.name}
          />
        )}
      </>
    );
  }
}

export default ProProfileContent;
